import { useState } from 'react';
import { Editor } from '@tiptap/react';

import { useStore } from 'utils/hooks/store';

import { getAiStorage, selectionOverlapsAiChange } from 'containers/Tiptap/lib/ai-utils';

interface UseToolbarStateProps {
  editor: Editor;
  onClose: () => void;
}

interface UseToolbarStateReturn {
  displayCommentInput: boolean;
  addComment: () => void;
  handleAddComment: (content: string) => void;
  displayAiInput: boolean;
  aiInput: string;
  setAiInput: (value: string) => void;
  handleAskAI: () => void;
  handleGenerate: () => void;
  anyAIActions: boolean;
  isGenerating: boolean;
  hasPending: boolean;
}

export const useToolbarState = ({ editor, onClose }: UseToolbarStateProps): UseToolbarStateReturn => {
  const { session } = useStore();

  // Comment state
  const [displayCommentInput, setDisplayCommentInput] = useState(false);

  // AI state
  const [displayAiInput, setDisplayAiInput] = useState(false);
  const [aiInput, setAiInput] = useState('');

  // AI actions and state
  const aiStorage = getAiStorage(editor);
  const isGenerating = aiStorage?.state === 'loading';
  const hasPending = aiStorage?.response !== undefined;
  const selectionIsAiChange = selectionOverlapsAiChange(editor);
  const anyAIActions =
    isGenerating ||
    (selectionIsAiChange && (editor.can().aiAccept() || editor.can().aiReject() || editor.can().aiRegenerate()));

  const addComment = (): void => {
    setDisplayCommentInput(true);
  };

  const handleAddComment = (content: string): void => {
    setDisplayCommentInput(false);
    editor
      .chain()
      .focus()
      .setThread({
        content,
        commentData: { user: { name: session?.user?.name, uuid: session?.user?.uuid } },
      })
      .run();

    onClose();
  };

  const handleAskAI = (): void => {
    setDisplayAiInput((prev) => !prev);
  };

  const handleGenerate = (): void => {
    // Auto-accept any pending AI response before starting a new prompt.
    // aiAccept only clears AI storage without touching the doc (given our
    // callers never set options.insert = false), so it's safe even if the
    // user manually deleted the previous AI content.
    if (hasPending) {
      editor
        .chain()
        .aiAccept()
        .command(({ commands }) => commands.stopTrackingAiChanges())
        .run();
    }

    editor
      .chain()
      .focus()
      .startTrackingAiChanges()
      .setShowAiChanges(false)
      .aiTextPrompt({
        text: aiInput,
        context: [
          {
            type: 'text',
            text: editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, ' '),
          },
        ],
        format: 'rich-text',
      })
      .run();

    setDisplayAiInput(false);
    setAiInput('');
  };

  return {
    // Comment state
    displayCommentInput,
    addComment,
    handleAddComment,

    // AI state
    displayAiInput,
    aiInput,
    setAiInput,
    handleAskAI,
    handleGenerate,

    // AI actions
    anyAIActions,
    isGenerating,
    hasPending,
  };
};
